export type LoadableAsset = {
  url: string;
  label: string;
};

export type LoadingProgress = {
  loadedBytes: number;
  totalBytes: number;
  loadedCount: number;
  totalCount: number;
};

const formatMb = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)} МБ`;

export class LoadingScreen {
  private el: HTMLDivElement;
  private fill: HTMLDivElement;
  private percent: HTMLDivElement;
  private bytes: HTMLDivElement;
  private label: HTMLDivElement;

  constructor(container: HTMLElement) {
    this.el = document.createElement('div');
    this.el.id = 'loading-screen';
    this.el.innerHTML = `
      <div class="loading-card">
        <div class="loading-kicker">GARDEN OF DOOM</div>
        <div class="loading-title">Загрузка арены</div>
        <div class="loading-meter">
          <div class="loading-fill"></div>
        </div>
        <div class="loading-row">
          <div class="loading-percent">0%</div>
          <div class="loading-bytes">0.0 МБ / 0.0 МБ</div>
        </div>
        <div class="loading-label">Подготовка...</div>
      </div>
    `;
    this.applyStyles();
    container.appendChild(this.el);
    this.fill = this.el.querySelector('.loading-fill')!;
    this.percent = this.el.querySelector('.loading-percent')!;
    this.bytes = this.el.querySelector('.loading-bytes')!;
    this.label = this.el.querySelector('.loading-label')!;
  }

  setProgress(progress: LoadingProgress): void {
    const ratio = progress.totalBytes > 0
      ? Math.min(progress.loadedBytes / progress.totalBytes, 1)
      : Math.min(progress.loadedCount / Math.max(progress.totalCount, 1), 1);
    const percent = Math.round(ratio * 100);

    this.fill.style.width = `${percent}%`;
    this.percent.textContent = `${percent}%`;
    this.bytes.textContent = progress.totalBytes > 0
      ? `${formatMb(progress.loadedBytes)} / ${formatMb(progress.totalBytes)}`
      : `${progress.loadedCount} / ${progress.totalCount}`;
  }

  setLabel(text: string): void {
    this.label.textContent = text;
  }

  hide(): void {
    this.el.classList.add('is-hidden');
    window.setTimeout(() => this.el.remove(), 280);
  }

  private applyStyles(): void {
    if (document.getElementById('loading-screen-styles')) return;
    const style = document.createElement('style');
    style.id = 'loading-screen-styles';
    style.textContent = `
      #loading-screen {
        position: absolute;
        inset: 0;
        z-index: 1000;
        display: grid;
        place-items: center;
        overflow: hidden;
        background:
          radial-gradient(circle at 50% 42%, rgba(202, 44, 39, .28), transparent 34%),
          linear-gradient(180deg, #170404 0%, #050506 56%, #010101 100%);
        color: #fff;
        font-family: Arial, sans-serif;
        transition: opacity .28s ease, transform .28s ease;
      }
      #loading-screen::before {
        content: '';
        position: absolute;
        inset: 0;
        background:
          linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px),
          linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px);
        background-size: 34px 34px;
        mask-image: radial-gradient(circle at center, #000 0%, transparent 72%);
      }
      #loading-screen.is-hidden {
        opacity: 0;
        transform: scale(1.02);
        pointer-events: none;
      }
      .loading-card {
        position: relative;
        width: min(520px, calc(100vw - 48px));
        padding: 34px;
        border-radius: 18px;
        background: rgba(10, 11, 14, .72);
        box-shadow: 0 24px 80px rgba(0,0,0,.62), inset 0 0 0 1px rgba(255,255,255,.1);
        backdrop-filter: blur(18px);
      }
      .loading-kicker {
        color: #ff6b5f;
        font-size: 13px;
        font-weight: 900;
        letter-spacing: 2px;
      }
      .loading-title {
        margin-top: 10px;
        font-size: 34px;
        line-height: 1;
        font-weight: 900;
        color: #fff;
        text-shadow: 0 0 28px rgba(255, 76, 67, .32);
      }
      .loading-meter {
        height: 16px;
        margin-top: 30px;
        overflow: hidden;
        border-radius: 999px;
        background: rgba(255,255,255,.1);
        box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
      }
      .loading-fill {
        width: 0%;
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, #ff3d35, #ffb14a);
        box-shadow: 0 0 24px rgba(255, 82, 64, .55);
        transition: width .16s ease;
      }
      .loading-row {
        display: flex;
        justify-content: space-between;
        gap: 18px;
        margin-top: 14px;
        font-size: 16px;
        font-weight: 800;
      }
      .loading-bytes {
        color: rgba(255,255,255,.72);
      }
      .loading-label {
        min-height: 20px;
        margin-top: 18px;
        color: rgba(255,255,255,.62);
        font-size: 14px;
        font-weight: 700;
      }
    `;
    document.head.appendChild(style);
  }
}

const readContentLength = async (url: string): Promise<number> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    if (!response.ok) return 0;
    return Number(response.headers.get('content-length')) || 0;
  } catch {
    return 0;
  }
};

const fetchAsset = async (
  asset: LoadableAsset,
  loadedByUrl: Map<string, number>,
  onProgress: () => void,
): Promise<void> => {
  const response = await fetch(asset.url);
  if (!response.ok) throw new Error(`Не удалось загрузить ${asset.label}`);
  if (!response.body) {
    const blob = await response.blob();
    loadedByUrl.set(asset.url, blob.size);
    onProgress();
    return;
  }

  const reader = response.body.getReader();
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    received += value.byteLength;
    loadedByUrl.set(asset.url, received);
    onProgress();
  }

  onProgress();
};

export const preloadAssetsWithProgress = async (
  assets: LoadableAsset[],
  onProgress: (progress: LoadingProgress) => void,
): Promise<void> => {
  const uniqueAssets = Array.from(new Map(assets.map(asset => [asset.url, asset])).values());
  const sizes = new Map<string, number>();
  const loadedByUrl = new Map<string, number>();

  await Promise.all(uniqueAssets.map(async asset => {
    sizes.set(asset.url, await readContentLength(asset.url));
  }));

  const totalBytes = Array.from(sizes.values()).reduce((sum, size) => sum + size, 0);
  let loadedCount = 0;
  const emit = () => {
    const loadedBytes = Array.from(loadedByUrl.values()).reduce((sum, size) => sum + size, 0);
    onProgress({
      loadedBytes,
      totalBytes,
      loadedCount,
      totalCount: uniqueAssets.length,
    });
  };

  emit();

  const queue = [...uniqueAssets];
  const worker = async () => {
    while (queue.length > 0) {
      const asset = queue.shift();
      if (!asset) return;
      await fetchAsset(asset, loadedByUrl, emit);
      loadedCount += 1;
      emit();
    }
  };

  await Promise.all(Array.from({ length: Math.min(4, uniqueAssets.length) }, worker));
};
